/* eslint-disable react-hooks/exhaustive-deps */
import { Checkbox, createStyles, FormControl, FormControlLabel, FormGroup, FormLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, FormatHours, formatNumber, getMaxTickLimit, getLabels, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartFilter } from '../ChartComponents';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartNotes } from '../ChartNotes';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import InsertChartOutlinedIcon from '@material-ui/icons/InsertChartOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        footer: {
            fontSize: '1rem',
            textAlign: 'center'
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        formControl: {
            display: 'flex',
            flexFlow: 'row wrap',
            alignItems: 'center',
            justifyContent: 'center'
        },
        formLabel: {
            marginRight: '10px',
        },
    }),
);

export enum TaskQueueByPeriodShowEnum {
    NUMBER_OF_TASKS = 0,
    ESTIMATED_EFFORTS,
}

type GroupByType = 'AssignedToName' | 'TaskTypeDescription' | 'Priority';

interface TaskQueueByPeriodChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    groupBy: GroupByType;
}

interface ChartState {
    options: TaskQueueByPeriodShowEnum;
    isNotYetStarted: boolean;
    isInprogress: boolean;
    taskQueueByGroup: TaskQueueByGroup[];
    totalCount: number;
    symbol: string;
}

export const TaskQueueByPeriodChart: React.FC<TaskQueueByPeriodChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const [state, setState] = useState<ChartState>({
        options: TaskQueueByPeriodShowEnum.NUMBER_OF_TASKS,
        isNotYetStarted: true,
        isInprogress: false,
        taskQueueByGroup: [],
        totalCount: 0,
        symbol: '$',
    });

    // tslint:disable-next-line:no-any
    const matterBillingQuery = useQuery<TaskQueueData>(TASK_QUEUE_BY_GROUP_QUERY, {
        variables: {
            groupBy: props.groupBy,
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
        fetchPolicy: 'cache-and-network',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        setState((prevState) => {
            let taskQueueByGroup: TaskQueueByGroup[] = [];
            let totalCount: number = 0;
            if (
                matterBillingQuery.data &&
                matterBillingQuery.data.statistics &&
                matterBillingQuery.data.statistics.tasks &&
                matterBillingQuery.data.statistics.tasks.taskQueue &&
                !matterBillingQuery.loading &&
                !matterBillingQuery.error
            ) {
                const {
                    taskQueueCombinedOrderByEffort,
                    taskQueueInProgressOrderByEffort,
                    taskQueueNotStartedOrderByEffort,
                    taskQueueCombinedOrderByTasks,
                    taskQueueInProgressOrderByTasks,
                    taskQueueNotStartedOrderByTasks,
                } = matterBillingQuery.data.statistics.tasks.taskQueue;

                if (state.options === TaskQueueByPeriodShowEnum.ESTIMATED_EFFORTS) {
                    if (state.isInprogress && !state.isNotYetStarted) {
                        taskQueueByGroup = taskQueueInProgressOrderByEffort.taskQueueByGroup;
                        totalCount = taskQueueInProgressOrderByEffort.totalEffortCount;
                    } else if (state.isNotYetStarted && !state.isInprogress) {
                        taskQueueByGroup = taskQueueNotStartedOrderByEffort.taskQueueByGroup;
                        totalCount = taskQueueNotStartedOrderByEffort.totalEffortCount;
                    } else {
                        taskQueueByGroup = taskQueueCombinedOrderByEffort.taskQueueByGroup;
                        totalCount = taskQueueCombinedOrderByEffort.totalEffortCount;
                    }
                } else {
                    if (state.isInprogress && !state.isNotYetStarted) {
                        taskQueueByGroup = taskQueueInProgressOrderByTasks.taskQueueByGroup;
                        totalCount = taskQueueInProgressOrderByTasks.totalTaskCount;
                    } else if (state.isNotYetStarted && !state.isInprogress) {
                        taskQueueByGroup = taskQueueNotStartedOrderByTasks.taskQueueByGroup;
                        totalCount = taskQueueNotStartedOrderByTasks.totalTaskCount;
                    } else {
                        taskQueueByGroup = taskQueueCombinedOrderByTasks.taskQueueByGroup;
                        totalCount = taskQueueCombinedOrderByTasks.totalTaskCount;
                    }
                }
            }

            return {
                ...prevState,
                taskQueueByGroup,
                totalCount,
            };
        });
        // tslint:disable-next-line: align
    }, [matterBillingQuery.data, matterBillingQuery.loading, matterBillingQuery.error, state.options, state.isInprogress, state.isNotYetStarted]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isTaskQueueByAssigneeChartOpen`);
        }
    };

    const handleTotalClick = () => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (state.options === TaskQueueByPeriodShowEnum.ESTIMATED_EFFORTS) {
                if (state.isInprogress && !state.isNotYetStarted) {
                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'EffortInProgress' });
                } else if (state.isNotYetStarted && !state.isInprogress) {
                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'EffortNotStarted' });
                } else {
                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'EffortCombined' });
                }
            } else {
                if (state.isInprogress && !state.isNotYetStarted) {
                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'TaskInProgress' });
                } else if (state.isNotYetStarted && !state.isInprogress) {
                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'TaskNotStarted' });
                } else {
                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'TaskCombined' });
                }
            }

            const _tempTitle = `Queue Depth by ${getGroupByTitle(state.options)}`;

            props.getSelectedItem(searchCriterias, true, _tempTitle, DrillDownType.taskSummary, 'Basic Details');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = event.target.name;
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                [fieldName]: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const getIncludeByTitle = (isInprogress: boolean, isNotYetStarted: boolean) => {
        if (isInprogress && isNotYetStarted) {
            return 'Not Started and In progress';
        } else if (isInprogress && !isNotYetStarted) {
            return 'Tasks in progress';
        } else {
            return 'Tasks not yet started';
        }
    };

    const getGroupByTitle = (option: TaskQueueByPeriodShowEnum) => {
        if (option === TaskQueueByPeriodShowEnum.NUMBER_OF_TASKS) {
            return 'Number of Tasks';
        } else {
            return 'Estimated Effort';
        }
    };

    const onCheckboxNotYetStartedChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            let isInprogress = prevState.isInprogress;
            const checked = event.target.checked;

            if (!checked && !state.isInprogress) {
                isInprogress = true;
            }

            return {
                ...prevState,
                isNotYetStarted: checked,
                isInprogress: isInprogress,
            };
        });
    };

    const onCheckboxInprogressChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            let isNotYetStarted = prevState.isNotYetStarted;
            const checked = event.target.checked;
            if (!checked && !state.isNotYetStarted) {
                isNotYetStarted = true;
            }

            return {
                ...prevState,
                isInprogress: checked,
                isNotYetStarted: isNotYetStarted,
            };
        });
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    const getGroupByName = (groupBy: GroupByType) => {
        if (groupBy === 'AssignedToName') {
            return 'Assignee';
        } else if (groupBy === 'TaskTypeDescription') {
            return 'Task Type';
        } else {
            return 'Priority';
        }
    };

    if (matterBillingQuery.loading) {
        return (
            <ChartSkeleton variant="bars" />
        );
    }

    const datasetData = state.taskQueueByGroup.map((billingByGroup: TaskQueueByGroup) =>
        // tslint:disable-next-line: radix
        state.options === TaskQueueByPeriodShowEnum.ESTIMATED_EFFORTS ? billingByGroup.estimatedEffortCount : parseInt(billingByGroup.taskCount),
    );

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<InsertChartOutlinedIcon />} title={<>Queue Depth by {getGroupByTitle(state.options)}</>} />
                <div className={`${classes.total} h2`} onClick={handleTotalClick}>
                    <AnimatedNumber value={state.totalCount} format={(amount) => getGroupByTitle(state.options) === 'Estimated Effort' ? `${formatNumber(amount)} ${FormatHours(state.totalCount)}` : `${formatNumber(state.totalCount)}`} />
                </div>
                <div className={classes.title}>{getIncludeByTitle(state.isInprogress, state.isNotYetStarted)}</div>
                <div className={classes.title}>
                    {`by ${getGroupByName(props.groupBy)}`}
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.taskQueueByGroup.map((billingByGroup: TaskQueueByGroup) => billingByGroup.groupedBy)[_index];

                            const searchCriterias = new Array<SearchCriteria>();
                            
                            if (props.groupBy === 'AssignedToName') {
                                searchCriterias.push({ property: 'AssignedToName', operator: 'eq', comparison: item });
                            } else if (props.groupBy === 'TaskTypeDescription') {
                                searchCriterias.push({ property: 'TaskTypeDescription', operator: 'eq', comparison: item });
                            } else {
                                searchCriterias.push({ property: 'Priority', operator: 'eq', comparison: item });
                            }

                            if (state.options === TaskQueueByPeriodShowEnum.ESTIMATED_EFFORTS) {
                                if (state.isInprogress && !state.isNotYetStarted) {
                                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'EffortInProgress' });
                                } else if (state.isNotYetStarted && !state.isInprogress) {
                                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'EffortNotStarted' });
                                } else {
                                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'EffortCombined' });
                                }
                            } else {
                                if (state.isInprogress && !state.isNotYetStarted) {
                                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'TaskInProgress' });
                                } else if (state.isNotYetStarted && !state.isInprogress) {
                                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'TaskNotStarted' });
                                } else {
                                    searchCriterias.push({ property: 'TaskStatus', operator: 'eq', comparison: 'TaskCombined' });
                                }
                            }

                            const _tempTitle = `Queue Depth by ${getGroupByTitle(state.options)}`;
                            props.getSelectedItem(searchCriterias, true, _tempTitle, DrillDownType.taskSummary, 'Basic Details');
                        }
                    }}
                    data={{
                        labels: getLabels(state.taskQueueByGroup.map((billingByGroup: TaskQueueByGroup) => billingByGroup.groupedBy)),
                        datasets: [
                            {
                                label: state.options === TaskQueueByPeriodShowEnum.ESTIMATED_EFFORTS ? 'Estimated Effort' : 'Number of Tasks',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: datasetData
                            },
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick(datasetData),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(datasetData),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                              dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);
                                        
                                        if (state.options === TaskQueueByPeriodShowEnum.ESTIMATED_EFFORTS) {
                                            const estimatedEffortCount = state.taskQueueByGroup
                                            .map((billingByGroup: TaskQueueByGroup) => billingByGroup.estimatedEffortCount)[dataIndex];
                                            return `${dataset.label}: ${formatNumber(currentValue)} ${FormatHours(estimatedEffortCount)}`;

                                        }
                                        return `${dataset.label}: ${formatNumber(currentValue)}`;
                                    },
                                    // tslint:disable-next-line: no-any no-shadowed-variable
                                    afterLabel: function (context: any) {
                                        const { dataIndex } = context;
                                        const estimatedEffortCount = state.taskQueueByGroup
                                            .map((billingByGroup: TaskQueueByGroup) => billingByGroup.estimatedEffortCount)[dataIndex];
                                        const numberOfTasks = state.taskQueueByGroup
                                            // tslint:disable-next-line: radix
                                            .map((billingByGroup: TaskQueueByGroup) => parseFloat(billingByGroup.taskCount))[dataIndex];
                                        if (state.options === TaskQueueByPeriodShowEnum.ESTIMATED_EFFORTS) {
                                            return [`Number of Tasks: ${formatNumber(numberOfTasks)}`];

                                        }
                                        return [`Estimated Effort: ${formatNumber(estimatedEffortCount)} ${FormatHours(estimatedEffortCount)}`];
                                    },
                                },
                            },
                        },
                    }}
                />
            </div>
            <FormControl component="div" className={classes.formControl}>
                <FormLabel component="legend" className={classes.formLabel}>
                    Show
                </FormLabel>
                <RadioGroup name="options" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={TaskQueueByPeriodShowEnum.NUMBER_OF_TASKS} control={<Radio color="primary" />} label="Number of Tasks" />
                    <FormControlLabel value={TaskQueueByPeriodShowEnum.ESTIMATED_EFFORTS} control={<Radio color="primary" />} label="Estimated effort" />
                </RadioGroup>
            </FormControl>
            <FormControl component="div" className={classes.formControl}>
                <FormLabel component="legend" className={classes.formLabel}>
                    Include
                </FormLabel>
                <FormGroup row={true}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checkedIcon={<CheckBoxIcon color="primary" />}
                                checked={state.isNotYetStarted}
                                onChange={onCheckboxNotYetStartedChanged}
                                name="isNotYetStarted"
                            />
                        }
                        label="Tasks not yet started"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checkedIcon={<CheckBoxIcon color="primary" />} 
                                checked={state.isInprogress} 
                                onChange={onCheckboxInprogressChanged} 
                                name="isInprogress" 
                            />
                        }
                        label="Tasks in progress"
                    />
                </FormGroup>
            </FormControl>
            <ChartNotes notes={['A default of 1:00 hours is used for tasks with no estimated effort.']} />
            {/* {state.matterBillingByGroup.length >= 10 && <span className={classes.span}>The top 10 are shown</span>} */}
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface TaskQueueData {
    statistics: Statistics;
}

export interface Statistics {
    tasks: Tasks;
}

export interface Tasks {
    taskQueue: TaskQueue;
}

export interface TaskQueue {
    taskQueueInProgressOrderByTasks: TaskQueue;
    taskQueueNotStartedOrderByTasks: TaskQueue;
    taskQueueCombinedOrderByTasks: TaskQueue;
    taskQueueInProgressOrderByEffort: TaskQueue;
    taskQueueNotStartedOrderByEffort: TaskQueue;
    taskQueueCombinedOrderByEffort: TaskQueue;
}

export interface TaskQueue {
    taskQueueByGroup: TaskQueueByGroup[];
    totalTaskCount: number;
    totalEffortCount: number;
}

export interface TaskQueueByGroup {
    groupedBy: string;
    taskCount: string;
    estimatedEffortCount: number;
}

const TASK_QUEUE_BY_GROUP_QUERY = gql`
query taskQueue($groupBy: TaskGroupBy, $entityType: Int, $entityGuid: String, $responsibility: Int) {
    statistics {
      tasks {
        taskQueue(groupBy:$groupBy,entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
          taskQueueInProgressOrderByTasks {
            taskQueueByGroup {
              groupedBy
              taskCount
              estimatedEffortCount
            }
            totalTaskCount
            totalEffortCount
          },
                  taskQueueNotStartedOrderByTasks {
            taskQueueByGroup {
              groupedBy
              taskCount
              estimatedEffortCount
            }
            totalTaskCount
            totalEffortCount
          },
                  taskQueueCombinedOrderByTasks {
            taskQueueByGroup {
              groupedBy
              taskCount
              estimatedEffortCount
            }
            totalTaskCount
            totalEffortCount
          },
                  taskQueueInProgressOrderByEffort {
            taskQueueByGroup {
              groupedBy
              taskCount
              estimatedEffortCount
            }
            totalTaskCount
            totalEffortCount
          },
                  taskQueueNotStartedOrderByEffort {
            taskQueueByGroup {
              groupedBy
              taskCount
              estimatedEffortCount
            }
            totalTaskCount
            totalEffortCount
          },
                  taskQueueCombinedOrderByEffort {
            taskQueueByGroup {
              groupedBy
              taskCount
              estimatedEffortCount
            }
            totalTaskCount
            totalEffortCount
          },
        }
      }
    }
  }
`;
